import React from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { MapPin } from 'lucide-react';

// Default coordinates for different cities
const CITY_COORDINATES = {
  // Florida
  'Miami': { lat: 25.7617, lng: -80.1918 },
  'Tampa': { lat: 27.9506, lng: -82.4572 },
  'Orlando': { lat: 28.5383, lng: -81.3792 },
  'Jacksonville': { lat: 30.3322, lng: -81.6557 },
  // Colorado
  'Denver': { lat: 39.7392, lng: -104.9903 },
  'Boulder': { lat: 40.0150, lng: -105.2705 },
  'Colorado Springs': { lat: 38.8339, lng: -104.8214 },
  // NYC
  'Manhattan': { lat: 40.7831, lng: -73.9712 },
  'Brooklyn': { lat: 40.6782, lng: -73.9442 },
  'Queens': { lat: 40.7282, lng: -73.7949 },
};

// Submarket offsets (small random offsets to differentiate properties)
const getSubmarketOffset = (submarket) => {
  // Generate consistent offset based on submarket name
  const hash = submarket.split('').reduce((a, b) => {
    a = ((a << 5) - a) + b.charCodeAt(0);
    return a & a;
  }, 0);
  
  return {
    lat: (hash % 100) / 10000,
    lng: ((hash >> 8) % 100) / 10000
  };
};

const PropertyMap = ({ property, height = "300px" }) => {
  // Get base coordinates from city
  const cityCoords = CITY_COORDINATES[property.city] || { lat: 39.8283, lng: -98.5795 };
  
  // Add submarket offset for uniqueness
  const offset = getSubmarketOffset(property.submarket || property.name);
  const position = [
    cityCoords.lat + offset.lat,
    cityCoords.lng + offset.lng
  ];

  return (
    <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm" style={{ height }}>
      <MapContainer
        center={position}
        zoom={14}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Marker position={position}>
          <Popup>
            <div className="text-sm">
              <strong>{property.name}</strong>
              <br />
              {property.address}
              <br />
              <span className="text-[#006AFF] font-semibold">
                ${property.property_value?.toLocaleString()}
              </span>
            </div>
          </Popup>
        </Marker>
      </MapContainer>
    </div>
  );
};

// Map component for multiple properties
export const PropertiesMap = ({ properties, height = "400px" }) => {
  if (!properties || properties.length === 0) {
    return (
      <div 
        className="rounded-lg border border-blue-500/30 bg-slate-800/50 flex items-center justify-center"
        style={{ height }}
      >
        <div className="text-gray-400 flex items-center">
          <MapPin className="h-5 w-5 mr-2" />
          No properties to display
        </div>
      </div>
    );
  }

  // Calculate center based on all properties
  const allCoords = properties.map(p => {
    const cityCoords = CITY_COORDINATES[p.city] || { lat: 39.8283, lng: -98.5795 };
    const offset = getSubmarketOffset(p.submarket || p.name);
    return [cityCoords.lat + offset.lat, cityCoords.lng + offset.lng];
  });

  const centerLat = allCoords.reduce((sum, c) => sum + c[0], 0) / allCoords.length;
  const centerLng = allCoords.reduce((sum, c) => sum + c[1], 0) / allCoords.length;

  return (
    <div className="rounded-lg overflow-hidden border border-blue-500/30" style={{ height }}>
      <MapContainer
        center={[centerLat, centerLng]}
        zoom={5}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {properties.map((property, index) => {
          const cityCoords = CITY_COORDINATES[property.city] || { lat: 39.8283, lng: -98.5795 };
          const offset = getSubmarketOffset(property.submarket || property.name);
          const position = [cityCoords.lat + offset.lat, cityCoords.lng + offset.lng];
          
          return (
            <Marker key={property.id || index} position={position}>
              <Popup>
                <div className="text-sm min-w-[200px]">
                  <strong className="block mb-1">{property.name}</strong>
                  <span className="text-gray-600 text-xs block">{property.address}</span>
                  <div className="mt-2 pt-2 border-t">
                    <span className="text-blue-600 font-bold">
                      ${property.property_value?.toLocaleString()}
                    </span>
                    {property.is_distressed && (
                      <span className="ml-2 text-red-500 text-xs font-semibold">
                        DISTRESSED
                      </span>
                    )}
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
};

export default PropertyMap;
